// JWT Token Service
// Handles fetching, storing, and validating JWT tokens

import { log } from '../utils/logger';

const TOKEN_STORAGE_KEY = 'curai_token';
const TOKEN_EXPIRY_KEY = 'curai_expiry';
const EXPIRY_BUFFER_MS = 60000; // 60 seconds buffer before actual expiry (increased for safety)

// Use sessionStorage instead of localStorage for security
const storage = sessionStorage;

// Request deduplication: prevent concurrent token fetches
let tokenFetchPromise = null;

const logWithPrefix = (message, data) => {
  log(`[JWT Service] ${message}`, data);
};

export const fetchJWTToken = async () => {
  const scriptElementApiKey = document.querySelector("script[data-embed]") || document.querySelector("script[data-curatu-api-key]");
  const apiKey = scriptElementApiKey?.getAttribute("data-embed")
    || scriptElementApiKey?.getAttribute("data-curatu-api-key")
    || process.env.REACT_APP_API_KEY;

  // If a fetch is already in progress, return the existing promise
  if (tokenFetchPromise) {
    logWithPrefix('Token fetch already in progress, returning existing promise');
    return tokenFetchPromise;
  }

  logWithPrefix('Fetching JWT token with widget token');

  const baseUrl = process.env.REACT_APP_BACKEND_URL;

  const headers = {
    'Content-Type': 'application/json',
  };

  // Add API key header if available (for customer domains)
  if (apiKey) {
    headers['x-api-key'] = apiKey;
  }

  // Create and store the fetch promise
  tokenFetchPromise = (async () => {
    try {
      // Generate widget token

      const response = await fetch(
        `${baseUrl}/token`,
        {
          method: 'POST',
          headers,
        }
      );

      if (!response.ok) {
        throw new Error(`Token fetch failed with status ${response.status}`);
      }

      const data = await response.json();

      const token = data;

      if (!token) {
        throw new Error('Token not found in response');
      }

      // Store token with 29 minute expiry (token is valid for 30 mins, refresh at 29)
      // Token is valid for 30 mins, we'll refresh at 29 mins (1 min buffer for safety)
      const expiryTime = Date.now() + (29 * 60 * 1000);
      storage.setItem(TOKEN_STORAGE_KEY, token);
      storage.setItem(TOKEN_EXPIRY_KEY, expiryTime.toString());

      logWithPrefix('JWT token fetched and stored successfully');
      return token;
    } catch (error) {
      logWithPrefix('Error fetching JWT token', error);
      throw error;
    } finally {
      // Clear the promise after completion (success or failure)
      tokenFetchPromise = null;
    }
  })();

  return tokenFetchPromise;
};

/**
 * Gets the cached JWT token from sessionStorage
 * @returns {string | null} The cached token or null
 */
export const getCachedJWTToken = () => {
  return storage.getItem(TOKEN_STORAGE_KEY);
};

/**
 * Checks if the cached token is still valid (not expired)
 * @returns {boolean} True if token is valid, false otherwise
 */
export const isTokenValid = () => {
  const token = getCachedJWTToken();
  const expiryTime = storage.getItem(TOKEN_EXPIRY_KEY);

  if (!token || !expiryTime) {
    logWithPrefix('Token validation failed: missing token or expiry');
    return false;
  }

  const now = Date.now();
  const expiry = parseInt(expiryTime, 10);
  const isValid = now < (expiry - EXPIRY_BUFFER_MS);
  logWithPrefix('Token validation result', { isValid, timeRemaining: expiry - now });
  return isValid;
};

/**
 * Gets a valid JWT token - returns cached if valid, fetches new if expired
 */
export const getValidJWTToken = async () => {
  // First check: if token is valid, return immediately without any async operations
  if (isTokenValid()) {
    const cachedToken = getCachedJWTToken();
    logWithPrefix('Using cached JWT token');
    return cachedToken;
  }

  // If there's already a fetch in progress, wait for it instead of starting a new one
  if (tokenFetchPromise) {
    logWithPrefix('Token fetch already in progress, waiting for it to complete');
    return tokenFetchPromise;
  }

  // Only fetch if token is invalid and no fetch is in progress
  logWithPrefix('Token expired or missing, fetching new token');
  return await fetchJWTToken();
}